import React, { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

interface ClassicUserManagementProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: ApiFetch;
}

interface UserSummary {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  tenant_code?: string | null;
  tenant_name?: string | null;
  is_active: boolean;
  created_at: string;
}

interface TenantSummary {
  id: number;
  tenant_code: string;
  tenant_name: string;
  is_active: boolean;
}

type ModalKind = 'create' | 'edit' | 'password' | 'delete' | null;

interface ModalState {
  kind: ModalKind;
  user?: UserSummary;
}

interface UserFormState {
  username: string;
  email: string;
  full_name: string;
  password: string;
  passwordConfirm: string;
  role: string;
  tenant_code: string | null;
}

interface PasswordFormState {
  new_password: string;
  confirm_password: string;
}

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'super_admin (システム管理者)' },
  { value: 'admin', label: 'admin (複数保育園管理可能)' },
  { value: 'nursery_admin', label: 'nursery_admin' },
  { value: 'nursery_user', label: 'nursery_user' },
  { value: 'nursery_viewer', label: 'nursery_viewer' },
  { value: 'viewer', label: 'viewer' },
  { value: 'user', label: 'user' },
];

const ClassicModal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}
  >
    <div
      style={{
        width: '520px',
        maxWidth: '90%',
        backgroundColor: '#f0f0f0',
        border: '2px outset #c0c0c0',
        boxShadow: '6px 6px 0 rgba(0,0,0,0.25)',
        fontFamily: 'MS UI Gothic, sans-serif',
      }}
    >
      <div
        style={{
          backgroundColor: '#0066cc',
          color: '#fff',
          padding: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 'bold',
        }}
      >
        <span>{title}</span>
        <button
          onClick={onClose}
          style={{
            backgroundColor: '#c0c0c0',
            border: '2px outset #c0c0c0',
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          ×
        </button>
      </div>
      <div style={{ padding: '16px', fontSize: '12px' }}>{children}</div>
    </div>
  </div>
);

const ClassicUserManagement: React.FC<ClassicUserManagementProps> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
}) => {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>({ kind: null });
  const [feedback, setFeedback] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<UserFormState>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    passwordConfirm: '',
    role: 'nursery_user',
    tenant_code: '',
  });
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
    new_password: '',
    confirm_password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [assignedTenants, setAssignedTenants] = useState<number[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  const isSuperAdmin = (currentUser?.role ?? '').toLowerCase() === 'super_admin';

  const requiresTenant = (role: string) => {
    // super_adminはテナント不要
    if (role === 'super_admin') return false;
    // adminは複数保育園選択可能（テナント選択UIは非表示）
    if (role === 'admin') return false;
    // その他はテナント必須
    return true;
  };

  const resetForms = () => {
    setUserForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      passwordConfirm: '',
      role: 'nursery_user',
      tenant_code: '',
    });
    setPasswordForm({ new_password: '', confirm_password: '' });
    setFormErrors(null);
  };

  const closeModal = () => {
    setModalState({ kind: null });
    resetForms();
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch('/api/admin/users');
      if (!response.ok) {
        const message = await getErrorMessage(response, 'ユーザー一覧の取得に失敗しました');
        throw new Error(message);
      }
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await apiFetch('/api/admin/tenants');
      if (!response.ok) {
        const message = await getErrorMessage(response, 'テナント一覧の取得に失敗しました');
        throw new Error(message);
      }
      const data = await response.json();
      setTenants(Array.isArray(data) ? data.filter((tenant) => tenant.is_active) : []);
    } catch (err) {
      // テナント取得失敗は致命的ではないので警告のみ
      console.warn('Failed to load tenants', err);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      setError('この画面へアクセスする権限がありません（super_admin専用機能）');
      return;
    }
    loadUsers();
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const handleOpenCreate = () => {
    resetForms();
    setAssignedTenants([]); // 複数保育園選択をリセット
    setModalState({ kind: 'create' });
  };

  const loadAssignedTenants = async (userId: number) => {
    try {
      setLoadingTenants(true);
      const response = await apiFetch(`/api/admin/users/${userId}/assigned-tenants`);
      if (!response.ok) {
        const message = await getErrorMessage(response, '割り当て済み保育園の取得に失敗しました');
        throw new Error(message);
      }
      const data = await response.json();
      const tenantIds = (data.assigned_tenants || []).map((t: any) => t.id);
      setAssignedTenants(tenantIds);
    } catch (err: any) {
      console.error('割り当て済み保育園の取得に失敗:', err);
      setAssignedTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const saveTenantAssignments = async (userId: number) => {
    try {
      setLoadingTenants(true);
      const response = await apiFetch(`/api/admin/users/${userId}/assign-tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_ids: assignedTenants }),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, '保育園の割り当てに失敗しました');
        throw new Error(message);
      }
      setFeedback('保育園の割り当てを保存しました');
      await loadAssignedTenants(userId);
    } catch (err: any) {
      setFormErrors('保育園の割り当てに失敗しました: ' + (err?.message || '不明なエラー'));
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleOpenEdit = async (user: UserSummary) => {
    setUserForm({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '',
      passwordConfirm: '',
      role: user.role,
      tenant_code: user.tenant_code ?? '',
    });
    setFormErrors(null);
    setModalState({ kind: 'edit', user });
    // adminロールの場合は割り当て済み保育園を読み込む
    if (user.role === 'admin') {
      await loadAssignedTenants(user.id);
    } else {
      setAssignedTenants([]);
    }
  };

  const handleOpenPassword = (user: UserSummary) => {
    setPasswordForm({ new_password: '', confirm_password: '' });
    setFormErrors(null);
    setModalState({ kind: 'password', user });
  };

  const handleOpenDelete = (user: UserSummary) => {
    setModalState({ kind: 'delete', user });
  };

  const validateUserForm = (isCreate: boolean): boolean => {
    if (!userForm.username.trim()) {
      setFormErrors('ユーザー名を入力してください');
      return false;
    }
    if (!userForm.email.trim()) {
      setFormErrors('メールアドレスを入力してください');
      return false;
    }
    if (isCreate && userForm.password.length < 8) {
      setFormErrors('パスワードは8文字以上で入力してください');
      return false;
    }
    if (isCreate && userForm.password !== userForm.passwordConfirm) {
      setFormErrors('パスワードが一致しません');
      return false;
    }
    if (!ROLE_OPTIONS.some((option) => option.value === userForm.role)) {
      setFormErrors('不正なロールが指定されました');
      return false;
    }
    if (requiresTenant(userForm.role) && !userForm.tenant_code) {
      setFormErrors('対象テナントを選択してください');
      return false;
    }
    setFormErrors(null);
    return true;
  };

  const handleCreateUser = async () => {
    if (!validateUserForm(true)) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        username: userForm.username.trim(),
        email: userForm.email.trim(),
        full_name: userForm.full_name.trim(),
        password: userForm.password,
        role: userForm.role,
        tenant_code: requiresTenant(userForm.role) ? userForm.tenant_code : null,
      };
      const response = await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'ユーザーの作成に失敗しました');
        throw new Error(message);
      }

      const createdUser = await response.json();

      // adminの場合、複数保育園を割り当て
      if (userForm.role === 'admin' && assignedTenants.length > 0) {
        try {
          await apiFetch(`/api/admin/users/${createdUser.id}/assign-tenants`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenant_ids: assignedTenants }),
          });
        } catch (assignErr: any) {
          // 割り当て失敗は警告のみ（ユーザーは作成済み）
          console.warn('保育園の割り当てに失敗しました:', assignErr);
        }
      }

      setFeedback(`ユーザー「${payload.username}」を作成しました。`);
      closeModal();
      await loadUsers();
    } catch (err: any) {
      setFormErrors(err?.message || 'ユーザーの作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!modalState.user) {
      return;
    }
    if (!validateUserForm(false)) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        username: userForm.username.trim(),
        email: userForm.email.trim(),
        full_name: userForm.full_name.trim(),
        role: userForm.role,
        tenant_code: requiresTenant(userForm.role) ? userForm.tenant_code : null,
      };
      const response = await apiFetch(`/api/admin/users/${modalState.user.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'ユーザー情報の更新に失敗しました');
        throw new Error(message);
      }
      setFeedback(`ユーザー「${modalState.user.username}」を更新しました。`);
      closeModal();
      await loadUsers();
    } catch (err: any) {
      setFormErrors(err?.message || 'ユーザー情報の更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!modalState.user) {
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setFormErrors('パスワードは8文字以上で入力してください');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setFormErrors('パスワードが一致しません');
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiFetch(`/api/admin/users/${modalState.user.id}/password`, {
        method: 'PUT',
        body: JSON.stringify({ new_password: passwordForm.new_password }),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'パスワードの更新に失敗しました');
        throw new Error(message);
      }
      setFeedback(`ユーザー「${modalState.user.username}」のパスワードを更新しました。`);
      closeModal();
    } catch (err: any) {
      setFormErrors(err?.message || 'パスワードの更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleUser = async (user: UserSummary) => {
    try {
      const response = await apiFetch(`/api/admin/users/${user.id}/toggle`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'ユーザー状態の変更に失敗しました');
        throw new Error(message);
      }
      setFeedback(`ユーザー「${user.username}」を${user.is_active ? '無効化' : '有効化'}しました。`);
      await loadUsers();
    } catch (err: any) {
      setError(err?.message || 'ユーザー状態の変更に失敗しました');
    }
  };

  const handleDeleteUser = async () => {
    if (!modalState.user) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiFetch(`/api/admin/users/${modalState.user.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'ユーザーの削除に失敗しました');
        throw new Error(message);
      }
      setFeedback(`ユーザー「${modalState.user.username}」を削除しました。`);
      closeModal();
      await loadUsers();
    } catch (err: any) {
      setFormErrors(err?.message || 'ユーザーの削除に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusTag = (user: UserSummary) => (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: user.is_active ? '#e0ffe0' : '#ffe0e0',
        border: `1px solid ${user.is_active ? '#33aa33' : '#cc4444'}`,
        fontSize: '11px',
      }}
    >
      {user.is_active ? '有効' : '無効'}
    </span>
  );

  const activeUsers = useMemo(() => users.length, [users]);

  const tenantOptions = useMemo(() => {
    const options = tenants.map((tenant) => ({
      value: tenant.tenant_code,
      label: `${tenant.tenant_name} (${tenant.tenant_code})`,
    }));
    return [{ value: '', label: '--- 選択してください ---' }, ...options];
  }, [tenants]);

  const currentRole = userForm.role;
  const tenantDisabled = !requiresTenant(currentRole);

  const renderUserForm = (isCreate: boolean) => {
    // adminの場合、複数保育園選択UIを表示するかどうか
    const shouldShowTenantAssignment = userForm.role === 'admin' &&
      (isCreate || (modalState.kind === 'edit' && modalState.user));

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>ユーザー名</span>
            <input
              type="text"
              value={userForm.username}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, username: e.target.value }))
              }
              disabled={!isCreate}
              style={{
                padding: '4px',
                border: '2px inset #c0c0c0',
                backgroundColor: isCreate ? '#fff' : '#e9e9e9',
              }}
            />
            {!isCreate && (
              <span style={{ fontSize: '10px', color: '#666' }}>
                ユーザー名は編集できません
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>メールアドレス</span>
            <input
              type="email"
              value={userForm.email}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, email: e.target.value }))
              }
              style={{ padding: '4px', border: '2px inset #c0c0c0' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>氏名</span>
            <input
              type="text"
              value={userForm.full_name}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, full_name: e.target.value }))
              }
              style={{ padding: '4px', border: '2px inset #c0c0c0' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>ロール</span>
            <select
              value={userForm.role}
              onChange={(e) => {
                const nextRole = e.target.value;
                setUserForm((prev) => ({
                  ...prev,
                  role: nextRole,
                  tenant_code: requiresTenant(nextRole) ? prev.tenant_code : '',
                }));
                // adminの場合、割り当て済み保育園をリセット（新規作成時）
                if (isCreate && nextRole === 'admin') {
                  setAssignedTenants([]);
                }
              }}
              style={{ padding: '4px', border: '2px inset #c0c0c0' }}
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>

          {/* テナント選択UI: adminの場合は非表示（代わりに「保育園割り当て」セクションを表示） */}
          {!shouldShowTenantAssignment && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>テナント</span>
              <select
                value={userForm.tenant_code ?? ''}
                onChange={(e) =>
                  setUserForm((prev) => ({ ...prev, tenant_code: e.target.value }))
                }
                disabled={tenantDisabled}
                style={{
                  padding: '4px',
                  border: '2px inset #c0c0c0',
                  backgroundColor: tenantDisabled ? '#e9e9e9' : '#fff',
                }}
              >
                {tenantOptions.map((option) => (
                  <option key={option.value ?? 'none'} value={option.value ?? ''}>
                    {option.label}
                  </option>
                ))}
              </select>
              {tenantDisabled && (
                <span style={{ fontSize: '10px', color: '#666' }}>
                  {userForm.role === 'super_admin'
                    ? 'super_admin はテナントの紐付けは不要です'
                    : 'テナントを選択してください'}
                </span>
              )}
            </label>
          )}

          {isCreate && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>パスワード</span>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) =>
                    setUserForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  style={{ padding: '4px', border: '2px inset #c0c0c0' }}
                  placeholder="8文字以上"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>パスワード（確認）</span>
                <input
                  type="password"
                  value={userForm.passwordConfirm}
                  onChange={(e) =>
                    setUserForm((prev) => ({ ...prev, passwordConfirm: e.target.value }))
                  }
                  style={{ padding: '4px', border: '2px inset #c0c0c0' }}
                  placeholder="再入力"
                />
              </label>
            </>
          )}

          {/* adminロールのユーザー新規作成・編集時に保育園割り当てUIを表示 */}
          {shouldShowTenantAssignment && (
            <div style={{ marginTop: '16px', padding: '12px', border: '2px inset #c0c0c0', backgroundColor: '#fff' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>保育園割り当て</div>
              <div style={{ fontSize: '10px', color: '#666', marginBottom: '12px' }}>
                このユーザーがアクセス可能な保育園を選択してください
              </div>
              {loadingTenants ? (
                <div style={{ fontSize: '11px', color: '#666' }}>読み込み中...</div>
              ) : (
                <>
                  {tenants.filter(t => t.is_active).map(tenant => (
                    <label
                      key={tenant.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={assignedTenants.includes(tenant.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignedTenants([...assignedTenants, tenant.id]);
                          } else {
                            setAssignedTenants(assignedTenants.filter(id => id !== tenant.id));
                          }
                        }}
                        style={{ marginRight: '6px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '11px' }}>
                        {tenant.tenant_name} ({tenant.tenant_code})
                      </span>
                    </label>
                  ))}
                  {tenants.filter(t => t.is_active).length === 0 && (
                    <div style={{ fontSize: '10px', color: '#666' }}>
                      利用可能な保育園がありません
                    </div>
                  )}
                  {!isCreate && modalState.user && (
                    <button
                      onClick={() => modalState.user && saveTenantAssignments(modalState.user.id)}
                      disabled={loadingTenants}
                      style={{
                        marginTop: '12px',
                        padding: '4px 12px',
                        backgroundColor: '#448aff',
                        border: '2px outset #c0c0c0',
                        color: '#fff',
                        cursor: loadingTenants ? 'not-allowed' : 'pointer',
                        opacity: loadingTenants ? 0.7 : 1,
                        fontSize: '11px',
                      }}
                    >
                      {loadingTenants ? '保存中...' : '保育園割り当てを保存'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {formErrors && (
          <div
            style={{
              marginTop: '12px',
              backgroundColor: '#ffe0e0',
              border: '1px solid #cc0000',
              padding: '6px 8px',
              color: '#cc0000',
            }}
          >
            {formErrors}
          </div>
        )}

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={closeModal}
            style={{
              padding: '6px 16px',
              backgroundColor: '#c0c0c0',
              border: '2px outset #c0c0c0',
              cursor: 'pointer',
            }}
          >
            キャンセル
          </button>
          <button
            onClick={isCreate ? handleCreateUser : handleUpdateUser}
            disabled={submitting}
            style={{
              padding: '6px 18px',
              backgroundColor: '#448aff',
              border: '2px outset #c0c0c0',
              color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? '処理中...' : isCreate ? '作成' : '更新'}
          </button>
        </div>
      </>
    );
  };

  const renderPasswordForm = () => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span>新しいパスワード</span>
          <input
            type="password"
            value={passwordForm.new_password}
            onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))}
            style={{ padding: '4px', border: '2px inset #c0c0c0' }}
            placeholder="8文字以上"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span>パスワード（確認）</span>
          <input
            type="password"
            value={passwordForm.confirm_password}
            onChange={(e) =>
              setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))
            }
            style={{ padding: '4px', border: '2px inset #c0c0c0' }}
          />
        </label>
      </div>

      {formErrors && (
        <div
          style={{
            marginTop: '12px',
            backgroundColor: '#ffe0e0',
            border: '1px solid #cc0000',
            padding: '6px 8px',
            color: '#cc0000',
          }}
        >
          {formErrors}
        </div>
      )}

      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          onClick={closeModal}
          style={{
            padding: '6px 16px',
            backgroundColor: '#c0c0c0',
            border: '2px outset #c0c0c0',
            cursor: 'pointer',
          }}
        >
          キャンセル
        </button>
        <button
          onClick={handleChangePassword}
          disabled={submitting}
          style={{
            padding: '6px 18px',
            backgroundColor: '#448aff',
            border: '2px outset #c0c0c0',
            color: '#fff',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? '処理中...' : '更新'}
        </button>
      </div>
    </>
  );

  const renderDeleteConfirm = () => (
    <>
      <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
        <p>
          ユーザー「<strong>{modalState.user?.username}</strong>」を削除します。
        </p>
        <p style={{ color: '#cc0000', fontWeight: 'bold' }}>
          この操作は元に戻せません。本当に削除してよろしいですか？
        </p>
      </div>

      {formErrors && (
        <div
          style={{
            marginTop: '12px',
            backgroundColor: '#ffe0e0',
            border: '1px solid #cc0000',
            padding: '6px 8px',
            color: '#cc0000',
          }}
        >
          {formErrors}
        </div>
      )}

      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          onClick={closeModal}
          style={{
            padding: '6px 16px',
            backgroundColor: '#c0c0c0',
            border: '2px outset #c0c0c0',
            cursor: 'pointer',
          }}
        >
          キャンセル
        </button>
        <button
          onClick={handleDeleteUser}
          disabled={submitting}
          style={{
            padding: '6px 18px',
            backgroundColor: '#d9534f',
            border: '2px outset #c0c0c0',
            color: '#fff',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? '削除中...' : '削除する'}
        </button>
      </div>
    </>
  );

  if (!isSuperAdmin) {
    return (
      <div
        style={{
          backgroundColor: '#f0f0f0',
          minHeight: '100vh',
          padding: '24px',
          fontFamily: 'MS UI Gothic, sans-serif',
        }}
      >
        <div
          style={{
            backgroundColor: '#fff5f5',
            border: '1px solid #ff9999',
            padding: '16px',
            color: '#cc0000',
          }}
        >
          この画面へアクセスする権限がありません（super_admin専用機能）
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        minHeight: '100vh',
        fontFamily: 'MS UI Gothic, sans-serif',
        fontSize: '12px',
        padding: '16px 24px 32px 24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>ユーザー管理</h2>
        </div>
        <button
          onClick={handleOpenCreate}
          style={{
            padding: '6px 16px',
            backgroundColor: '#c0c0c0',
            border: '2px outset #c0c0c0',
            cursor: 'pointer',
          }}
        >
          新規作成
        </button>
      </div>

      <div
        style={{
          backgroundColor: '#ffffff',
          border: '2px inset #c0c0c0',
          padding: '12px',
          marginBottom: '12px',
        }}
      >
        <div style={{ fontSize: '11px', color: '#333' }}>
          <strong>登録件数:</strong> {activeUsers} 件
        </div>
        {feedback && (
          <div
            style={{
              marginTop: '8px',
              backgroundColor: '#e8f5e9',
              border: '1px solid #4caf50',
              padding: '6px 8px',
              color: '#2e7d32',
            }}
          >
            {feedback}
          </div>
        )}
        {error && (
          <div
            style={{
              marginTop: '8px',
              backgroundColor: '#ffe0e0',
              border: '1px solid #cc0000',
              padding: '6px 8px',
              color: '#cc0000',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div
        style={{
          backgroundColor: '#ffffff',
          border: '2px inset #c0c0c0',
          minHeight: '240px',
          overflow: 'auto',
        }}
      >
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '12px',
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#e0e0e0' }}>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080', width: '60px' }}>ID</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080' }}>ユーザー名</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080' }}>氏名</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080' }}>ロール</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080' }}>テナント</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080', width: '80px' }}>状態</th>
                <th style={{ padding: '8px', borderBottom: '2px solid #808080', width: '220px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr
                  key={user.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f7f7f7',
                    borderBottom: '1px solid #d0d0d0',
                  }}
                >
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{user.id}</td>
                  <td style={{ padding: '6px 8px' }}>{user.username}</td>
                  <td style={{ padding: '6px 8px' }}>{user.full_name || '-'}</td>
                  <td style={{ padding: '6px 8px' }}>{user.role}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {user.role === 'super_admin'
                      ? '全テナント'
                      : user.tenant_name || user.tenant_code || '未設定'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>{renderStatusTag(user)}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleOpenEdit(user)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#c0c0c0',
                          border: '2px outset #c0c0c0',
                          cursor: 'pointer',
                        }}
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleOpenPassword(user)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#c0c0c0',
                          border: '2px outset #c0c0c0',
                          cursor: 'pointer',
                        }}
                      >
                        パスワード
                      </button>
                      <button
                        onClick={() => handleToggleUser(user)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#c0c0c0',
                          border: '2px outset #c0c0c0',
                          cursor: 'pointer',
                        }}
                      >
                        {user.is_active ? '無効化' : '有効化'}
                      </button>
                      <button
                        onClick={() => handleOpenDelete(user)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#d9534f',
                          border: '2px outset #d9534f',
                          color: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    ユーザーが登録されていません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <button
          onClick={() => onMenuSelect('main-menu')}
          style={{
            padding: '6px 16px',
            backgroundColor: '#c0c0c0',
            border: '2px outset #c0c0c0',
            cursor: 'pointer',
          }}
        >
          メインメニューに戻る
        </button>
      </div>

      {modalState.kind === 'create' && (
        <ClassicModal title="ユーザー新規作成" onClose={closeModal}>
          {renderUserForm(true)}
        </ClassicModal>
      )}

      {modalState.kind === 'edit' && modalState.user && (
        <ClassicModal title={`ユーザー編集: ${modalState.user.username}`} onClose={closeModal}>
          {renderUserForm(false)}
        </ClassicModal>
      )}

      {modalState.kind === 'password' && modalState.user && (
        <ClassicModal title={`パスワード変更: ${modalState.user.username}`} onClose={closeModal}>
          {renderPasswordForm()}
        </ClassicModal>
      )}

      {modalState.kind === 'delete' && modalState.user && (
        <ClassicModal title="ユーザー削除確認" onClose={closeModal}>
          {renderDeleteConfirm()}
        </ClassicModal>
      )}
    </div>
  );
};

export default ClassicUserManagement;
